import React, {useEffect, useState} from 'react';
import {useOktaAuth} from '@okta/okta-react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    useToastProvider
} from '@deere/fuel-react';
import {Controller, useForm} from "react-hook-form";
import {useHistory, useParams} from 'react-router-dom';
import {Loading} from "../shared/Loading";
import {generateDisplayName} from "../shared/utils";
import {useAuthConfig} from "../shared/helper";
import {DataGrid} from "@mui/x-data-grid";
import {Chip} from "@material-ui/core";
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from "@material-ui/icons/Warning";
import {makeStyles} from '@material-ui/core/styles';
import {TEAM_LEAD_TEXT} from "../shared/Constants";
import {Block} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    radio: {
        '&$checked': {
            color: '#b3b3b3'
        }
    },
    notDisabled: {
        color: '#1b1b1b'
    },
    checked: {}
}));

export const TeamsView = ({name}) => {
    const classes = useStyles();
    let history = useHistory();
    let authAxios = useAuthConfig();
    const {authState} = useOktaAuth();
    const {enqueueToast} = useToastProvider();
    const {control} = useForm();
    let {teamId} = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const initState = {
        functionalAreaType: "",
        supplierNumber: "",
        supplierName: "",
        fiscalYear: "",
        teamMembers: [],
        subFunctionalArea: "",
        teamLeaderData: "",
        supplierInd: "",
        supNumber: "",
        supplierLocation: ""
    }
    const [state, setState] = useState(initState);
    const [selectedData, setSelectedData] = useState("");
    const initSbmData = [{value: "1", label: "SBM", user: "", valid: true},
        {value: "2", label: "SRO", user: "", valid: true},
        {value: "3", label: "GRO", user: "", valid: true}]
    const [sbmData, setSbmData] = useState(initSbmData);
    const [errorDesc, setErrorDesc] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [alertVariant, setAlertVariant] = useState("error");
    const [isFunctionalAreaError, setIsFunctionalAreaError] = useState(false);
    const [functionalAreaAlertVariant, setFunctionalAreaAlertVariant] = useState("error");
    const [functionalAreaErrorDesc, setFunctionalAreaErrorDesc] = useState("");

    const resetState = () => {
        setState(initState);
    }

    const onCancel = () => {
        resetState();
        history.push("/rating-teams/");
    }

    const onEdit = () => {
        history.push(`/rating-teams/edit-team/${state.supNumber}`);
    }

    const processData = data => {
        setState({
            ...state,
            functionalAreaType: data.functionalAreaType,
            supplierNumber: parseInt(data.supplierNumber),
            supplierName: data.supplierName,
            fiscalYear: data.fiscalYear,
            supplyBaseManger: data.supplyBaseManger,
            teamMembers: data.teamMembers.filter(item => item.teamLeader !== "Y"),
            subFunctionalArea: data.subFunctionalArea ? data.subFunctionalArea.description : "-",
            teamLeaderData: data.teamMembers.filter(item => item.teamLeader === "Y"),
            supplierInd: data.supplierInd,
            supNumber: data.supplierNumber,
            supplierLocation: data.supplierLocation,
            isFunctionalAreaSROMatched: data.isFunctionalAreaSROMatched
        });

        const initialSbmData = [
            {
                value: `SBM-${data.sbm.userId}`,
                label: "SBM:",
                user: generateDisplayName(data.sbm.firstName, data.sbm.lastName, data.sbm.userId),
                valid: true
            },
            {
                value: `SRO-${data.sro.userId}`,
                label: "SRO:",
                user: generateDisplayName(data.sro.firstName, data.sro.lastName, data.sro.userId),
                valid: true
            },
            {
                value: `GRO-${data.gro.userId}`,
                label: "GRO:",
                user: generateDisplayName(data.gro.firstName, data.gro.lastName, data.gro.userId),
                valid: true
            }
        ]
        const selectedData = data.teamMembers.filter(item => item.teamLeader === "Y");

        if (data.isOldLeadPresent === "Y") {
            let selectedRole = ""

            if (selectedData[0]?.role?.id === "63")
                selectedRole = "SRO"
            else if (selectedData[0]?.role?.id === "64")
                selectedRole = "SBM"
            else if (selectedData[0]?.role?.id === "65")
                selectedRole = "GRO"

            const updatedSbmData = initialSbmData.map(item => {
                const value = item.value.split('-');
                if (value[0] === selectedRole) {
                    item.valid = false
                }
                return item
            })
            setSbmData(updatedSbmData)
            setSelectedData(`${selectedRole}-${data.newUser.racfID}`)
            setErrorMsg("user updated")
            setAlertVariant("warning")
            setErrorDesc(`${generateDisplayName(data.newUser.firstName, data.newUser.lastName, data.newUser.racfID)} 
                            replaced ${selectedData[0].name} - ${selectedData[0].racfId} as ${selectedRole}. Click Edit to Update`)
        } else {
            setErrorMsg("")
            setAlertVariant("error")
            setErrorDesc("")
            setSbmData(initialSbmData)
            if (selectedData[0]?.role?.id === "63") {
                setSelectedData(`SRO-${selectedData[0].racfId}`)
            }
            if (selectedData[0]?.role?.id === "64") {
                setSelectedData(`SBM-${selectedData[0].racfId}`)
            }
            if (selectedData[0]?.role?.id === "65") {
                setSelectedData(`GRO-${selectedData[0].racfId}`)
            }
        }

        if (data.isFunctionalAreaSROMatched === false) {
            setIsFunctionalAreaError(true)
            setFunctionalAreaAlertVariant("warning")
            setFunctionalAreaErrorDesc(`Functional area from SRO (${data.sroFunctionalAreaType}) is different from AE supplier functional Area.`)
        }

        setIsLoading(false)
    }

    const getTeamDetails = async () => {
        try {
            return await authAxios.get(`/teams/${teamId}`);
        } catch (e) {
            console.error(e);
            setIsLoading(false)
            enqueueToast({
                variant: "error",
                title: "Error fetching teams details",
                message: e.response ? e.response.data[e.response.status.toString()] : "",
                isPersistent: true
            });
        }
    };

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            hide: true
        },
        {
            field: 'name',
            headerName: 'Stakeholder',
            flex: 1,
            renderCell: (params) => {
                let userName = params.value
                return (

                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Typography display='block' variant="body1">
                                {userName}
                            </Typography>
                            {params.row?.userStatus && params.row?.userStatus !== "ACTIVE" && <Tooltip title={"Invalid User ID"}><Block htmlColor={"red"} /></Tooltip>}
                        </div>
                );
            }
        },
        {
            field: 'role',
            headerName: 'Role',
            flex: 1,
            renderCell: (params) => {
                let userRole = params.value && params.value.description
                return (
                    <>
                        <Typography display='block' variant="body1">
                            {userRole}
                        </Typography>
                    </>
                );
            }
        },
        {
            field: 'unit',
            headerName: 'Unit',
            flex: 1,
            renderCell: (params) => {
                let unit = params.value && `${params.value.description} - ${params.value.id}`
                return (
                    <>
                        <Typography display='block' variant="body1">
                            {unit}
                        </Typography>
                    </>
                );
            }
        }
    ];

    const rowsData = state.teamMembers.map((item) => {
        return {
            id: item.id,
            name: item.name,
            role: item.role,
            unit: item.unit,
            userStatus: item.userStatus
        };
    });

    useEffect(() => {
        if (teamId) {
            getTeamDetails().then(res => {
                if (res)
                    processData(res.data)
                setIsLoading(false)
            })
        }

        return () => {
            resetState()
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <React.Fragment>
            <Grid container alignItems="center">
                <Grid item xs={12} md={9}>
                    <Typography variant="h1" component="h1">
                        {name} - {state.supplierNumber}
                    </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Box display="flex" justifyContent="flex-end"
                         alignItems="flex-end">
                        <Box mr={4}>
                            <Button variant="secondary" size="large" onClick={() => {
                                onCancel()
                            }}>Cancel</Button>

                        </Box>
                        <Button variant="primary" size="large" onClick={() => {
                            onEdit()
                        }}>Edit</Button>

                    </Box>
                </Grid>
            </Grid>
            <Grid>
                <Grid item xs={12}>
                    <>
                        {isLoading && (
                            <Loading/>
                        )}
                        {!isLoading && (
                            <Box m={4}>
                                <Grid container>
                                    {errorMsg !== "" && (
                                        <Grid item xs={12}>
                                            <Box pt={0} mb={2} mt={4}>
                                                <Alert variant={alertVariant}
                                                       title={alertVariant === "warning" ? "" : errorMsg}>
                                                    {errorDesc && errorDesc}
                                                </Alert>
                                            </Box>
                                        </Grid>
                                    )}
                                    {isFunctionalAreaError && (
                                        <Grid item xs={12}>
                                            <Box pt={0} mb={2} mt={4}>
                                                <Alert variant={functionalAreaAlertVariant}>
                                                    {functionalAreaErrorDesc && functionalAreaErrorDesc}
                                                </Alert>
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item xs={8}>
                                        <Box mr={4} mt={4}>
                                            <Card>
                                                <CardContent>
                                                    <Grid container>
                                                        <Grid item xs={10}>
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle1" gutterBottom>
                                                                    Supplier
                                                                </Typography>
                                                                <Typography variant="body1" fontWeight="semibold"
                                                                            gutterBottom>
                                                                    {state.supplierName.toUpperCase()} - {state.supplierNumber}
                                                                </Typography>

                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={2}>
                                                            <Box display="flex" justifyContent="flex-end">
                                                                {state.supplierInd === "Y" ?
                                                                    <Chip size="small"
                                                                          icon={<CheckCircleIcon
                                                                              style={{fill: "#367c2b"}}/>}
                                                                          label={"AE Supplier"}
                                                                          style={{
                                                                              backgroundColor: "#D2E1CF",
                                                                              fontWeight: 600,
                                                                              borderRadius: ".5rem"
                                                                          }}/>
                                                                    :
                                                                    <>
                                                                        {state.supplierInd === "N" ?
                                                                            <Chip
                                                                                size="small"
                                                                                icon={<ErrorIcon
                                                                                    style={{fill: "#C21020"}}/>}
                                                                                label={"Non AE Supplier"}
                                                                                style={{
                                                                                    backgroundColor: "#F1C8CC",
                                                                                    fontWeight: 600,
                                                                                    borderRadius: ".5rem"
                                                                                }}/>
                                                                            :
                                                                            <></>
                                                                        }
                                                                    </>
                                                                }
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box mt={4}>
                                                                <Typography variant="label" gutterBottom>
                                                                    Location
                                                                </Typography>
                                                                <Typography variant="body1" fontWeight="semibold"
                                                                            gutterBottom>
                                                                    {state.supplierLocation}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box mt={4}>
                                                                <Typography variant="subtitle1" gutterBottom>
                                                                    Functional Area
                                                                </Typography>
                                                                <Typography variant="body1" fontWeight="semibold"
                                                                            gutterBottom>
                                                                    {state.functionalAreaType}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <Box mt={4}>
                                                                <Typography variant="subtitle1" gutterBottom>
                                                                    Sub Functional Area
                                                                </Typography>
                                                                <Typography variant="body1" fontWeight="semibold"
                                                                            gutterBottom>
                                                                    {state.subFunctionalArea}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box mt={4}>
                                            <Card style={{height: '155px'}}>
                                                <CardContent>
                                                    <Typography variant="label" gutterBottom>
                                                        {TEAM_LEAD_TEXT}
                                                    </Typography>
                                                    <Grid item xs={12}>
                                                        <Box p={1}>
                                                            <FormControl required fullWidth p={2} component="fieldset">
                                                                <Controller
                                                                    rules={{required: true}}
                                                                    control={control}
                                                                    defaultValue={selectedData}
                                                                    name={"teamLead"}
                                                                    render={({onChange, value}) => (
                                                                        <RadioGroup
                                                                            aria-label={name}
                                                                            name={name}
                                                                            value={selectedData}
                                                                            onChange={(e) => {
                                                                                onChange(e);
                                                                            }}>
                                                                            {sbmData && sbmData.map((item) => {
                                                                                return (
                                                                                    <FormControlLabel
                                                                                        key={item.value}
                                                                                        value={item.value}
                                                                                        control={<Radio disabled
                                                                                                        readOnly
                                                                                                        classes={{
                                                                                                            root: classes.radio,
                                                                                                            checked: classes.checked
                                                                                                        }}
                                                                                        />}
                                                                                        label={<>
                                                                                            <Typography
                                                                                                variant={"body1"}
                                                                                                display="inline">
                                                                                                {!item.valid && (
                                                                                                    <WarningIcon
                                                                                                        style={{
                                                                                                            fill: "#fab64c",
                                                                                                            paddingRight: '.25rem'
                                                                                                        }}/>
                                                                                                )}
                                                                                            </Typography>
                                                                                            <Typography
                                                                                                variant={"body1"}
                                                                                                fontWeight={"semibold"}
                                                                                                display="inline"
                                                                                                style={{color: '#1b1b1b'}}> {item.label}</Typography>
                                                                                            <Typography
                                                                                                variant={"body1"}
                                                                                                display="inline"
                                                                                                style={{color: '#1b1b1b'}}> {item.user}</Typography>
                                                                                        </>}
                                                                                    />
                                                                                );
                                                                            })}
                                                                        </RadioGroup>
                                                                    )}
                                                                />
                                                            </FormControl>
                                                        </Box>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box mt={4}>
                                            <Card>
                                                <CardContent>
                                                    <Box mt={2}>
                                                        <FormControl fullWidth>
                                                            <DataGrid
                                                                rows={rowsData}
                                                                columns={columns}
                                                                hideFooterPagination
                                                                hideFooterSelectedRowCount
                                                                disableSelectionOnClick
                                                                disableColumnSelector={true}
                                                                disableColumnFilter
                                                                autoHeight={true}
                                                            />
                                                        </FormControl>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
